import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import RouteSEO from './components/seo/RouteSEO';
import PrivateRoute from './components/common/PrivateRoute';
import ScrollToTop from './components/common/ScrollToTop';
import TicketManagement from './components/admin/ticketManagement/TicketManagement';
import AdminCampaignDashboard from './components/admin/fundraiseCampaignManagement/AdminCampaignDashboard';
import AccountDashboard from './components/admin/accountingManagement/AccountDashboard';
import AdminEventManagement from './components/admin/eventManagement/AdminEventManagement';
import AdminDisputeDashboard from './components/admin/disputeManagement/AdminDisputeDashboard';
import SignupPage from './pages/auth/SignupPage';

// Lazy-loaded pages/components (code splitting)
const WelcomePage = lazy(() => import('./pages/landing/WelcomePage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const EmailVerificationPage = lazy(() => import('./pages/auth/EmailVerificationPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const ServicePlansPage = lazy(() => import('./pages/dashboard/ServicePlansPage'));
const GroupDetailsPage = lazy(() => import('./pages/groups/GroupDetailsPage'));
const JoinGroupPage = lazy(() => import('./pages/groups/JoinGroupPage'));
const CreateGroupPage = lazy(() => import('./pages/groups/CreateGroupPage'));
const EnrolledGroupsPage = lazy(() => import('./pages/groups/EnrolledGroupsPage'));
const TransactionsPage = lazy(() => import('./pages/dashboard/TransactionsPage'));
const ProfilePage = lazy(() => import('./pages/settings/ProfilePage'));
const FundRequestHistoryPage = lazy(() => import('./pages/groups/FundRequestHistoryPage'));
const FundRequestDetailsPage = lazy(() => import('./pages/groups/FundRequestDetailsPage'));
const NotificationsPage = lazy(() => import('./pages/notifications/NotificationsPage'));
const NotificationDetailPage = lazy(() => import('./pages/notifications/NotificationDetailPage'));

const ArticlesPage = lazy(() => import('./pages/resources/articlesPage'));
const ArticleDetailPage = lazy(() => import('./pages/resources/articleDetailPage'));
const CreditManagementToolsPage = lazy(() => import('./pages/resources/creditManagementToolsPage'));
const BudgetManagementToolsPage = lazy(() => import('./pages/resources/budgetManagementToolsPage'));
const DebtManagementToolsPage = lazy(() => import('./pages/resources/debtManagementToolsPage'));
const RetirementCalculatorPage = lazy(() => import('./pages/resources/retirementCalculatorPage'));
const MortgageCalculatorPage = lazy(() => import('./pages/resources/mortgageCalculatorPage'));
const ContactUsPage = lazy(() => import('./pages/dashboard/ContactUsPage'));
const ContactUsLandingPage = lazy(() => import('./pages/landing/ContactUsLandingPage'));
const AboutUsPage = lazy(() => import('./pages/landing/AboutUsPage'));
const FAQPage = lazy(() => import('./pages/static/FAQPage'));
const PricingAndFeesPage = lazy(() => import('./pages/static/PricingAndFeesPage'));
const CareersPage = lazy(() => import('./pages/static/CareersPage'));
const WebConferencePage = lazy(() => import('./pages/VideoConference/test/WebConferencePage'));
const TestPage = lazy(() => import('./pages/VideoConference/test/test'));
const StripeConnectRefreshPage = lazy(() => import('./pages/payment/StripeConnectRefreshPage'));
const AccountProfileSetup = lazy(() => import('./pages/onboarding/AccountProfileSetup'));
const OrganizerProfileSetup = lazy(() => import('./pages/onboarding/OrganizerProfileSetup'));
const OrganizerProfilesList = lazy(() => import('./pages/onboarding/OrganizerProfilesList'));

const FundRaiseCampaignsPage = lazy(() => import('./pages/fundraise/FundRaiseCampaignsPage'));
const CampaignDetailsPage = lazy(() => import('./pages/fundraise/CampaignDetailsPage'));
const MyCampaigns = lazy(() => import('./components/fundsCampaign/myCampaigns/MyCampaigns'));
const PublicFundRaiseCampaignsPage = lazy(() => import('./pages/fundraise/PublicFundRaiseCampaignsPage'));
const PublicCampaignDetailsPage = lazy(() => import('./pages/fundraise/PublicCampaignDetailsPage'));
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'));

const EmployeeManagement = lazy(() => import('./components/admin/employeeManagement/EmployeeManagement'));
const UserManagement = lazy(() => import('./components/admin/userManagement/UserManagement'));
const GoogleAuthRegistration = lazy(() => import('./components/admin/employeeManagement/GoogleAuthenticatorSetup'));
const NewAdminLogin = lazy(() => import('./components/admin/NewAdminLogin'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminGroupManagement = lazy(() => import('./components/admin/groupManagement/AdminGroupManagement'));
const NexusReportPage = lazy(() => import('./components/admin/nexus/NexusReportPage'));
// Nexus alerts/obligations are now rendered inside `NexusReportPage` via tabs
const SubmittedMembershipRequests = lazy(() => import('./pages/groups/SubmittedMembershipRequests'));
const VideoConferenceApp = lazy(() => import('./pages/VideoConference/VideoConferenceApp'));
const LessonPage = lazy(() => import('./components/lms/LessonPage'));
const EducativeGroupDetails = lazy(() => import('./components/lms/EducativeGroupDetails'));
const EventsPage = lazy(() => import('./pages/events/EventsPage'));
const EventDetailsPage = lazy(() => import('./pages/events/EventDetailsPage'));
const EventSponsorshipPage = lazy(() => import('./pages/events/EventSponsorshipPage'));
const EventCreationPage = lazy(() => import('./pages/events/EventCreationPage'));
const UserEventsPage = lazy(() => import('./pages/events/UserEventsPage'));
const OrganiserEventManagementDashboard = lazy(() => import('./pages/events/OragniserEventManagement'));
const RegisteredEventsPage = lazy(() => import('./pages/events/RegisteredEventsPage'));
const RetryTicketPurchasePage = lazy(() => import('./pages/events/RetryTicketPurchasePage'));
const PublicEventsPage = lazy(() => import('./pages/events/PublicEventsPage'));
const PublicEventDetailsPage = lazy(() => import('./pages/events/PublicEventDetailsPage'));
const CreateFundraiserCampaignPage = lazy(() => import('./pages/fundraise/CreateFundraiserCampaignPage'));
const GroupCreationTutorial = lazy(() => import('./pages/static/groupTutorials/GroupCreationTutorial'));
const ManageMembersTutorial = lazy(() => import('./pages/static/groupTutorials/ManageMembersTutorial'));
const GroupManagementTutorial = lazy(() => import('./pages/static/groupTutorials/GroupManagementTutorial'));
const EventCreationTutorial = lazy(() => import('./pages/static/eventTutorial/EventCreationTutorial'));
const PrivacyPolicyPage = lazy(() => import('./pages/static/legal/privacy'));
const DisclosurePolicyPage = lazy(() => import('./pages/static/legal/disclosure'));
const TermsAndConditionsPage = lazy(() => import('./pages/static/legal/termsAndConditions'));
const AmlCtfPage = lazy(() => import('./pages/static/legal/aml-ctf'));
const SecurityPolicyPage = lazy(() => import('./pages/static/legal/security'));
const CookiePolicyPage = lazy(() => import('./pages/static/legal/cookie'));
const AcceptableUsePolicyPage = lazy(() => import('./pages/static/legal/acceptableUsePolicy'));

// Dispute pages
const MyDisputesPage = lazy(() => import('./pages/disputes/MyDisputesPage'));
const DisputeDetailsPage = lazy(() => import('./pages/disputes/DisputeDetailsPage'));

const LoadingFallback = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
    <CircularProgress size={28} />
  </Box>
);

const AppRoutes = ({ darkMode, toggleDarkMode }) => {
  return (
    <>
      <RouteSEO />
      <ScrollToTop behavior="auto" />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>

          {/* admin test routes. Delete these*/}
          {/* <Route path="/admin/tickets" element={<TicketManagement />} />
            <Route path="/admin/accounting" element={<AccountDashboard />} />
            <Route path="/admin/fundraise" element={<AdminCampaignDashboard />} /> */}


          {/* End of admin test routes*/}

          {/* user test routes. Delete these*/}
          <Route path="/events/sponsor" element={<EventSponsorshipPage />} />
          {/* End of user test routes*/}


          <Route path="/" element={<WelcomePage />} />
          <Route path="/about-us" element={<AboutUsPage />} />
          <Route path="/contact-us" element={<ContactUsLandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email/:otp/:id" element={<EmailVerificationPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/pricing" element={<PricingAndFeesPage />} />
          {/* Stripe Connect refresh page (public). Stripe will redirect here when link expires */}
          <Route path="/stripe/refresh" element={<StripeConnectRefreshPage />} />

          <Route path="/resources/articles" element={<ArticlesPage />} />
          <Route path="/resources/articles/:slug" element={<ArticleDetailPage />} />
          <Route path="/resources/debt-management" element={<DebtManagementToolsPage />} />
          <Route path="/resources/credit-tools" element={<CreditManagementToolsPage />} />
          <Route path="/resources/budget-tools" element={<BudgetManagementToolsPage />} />
          <Route path="/resources/retirement-calculator" element={<RetirementCalculatorPage />} />
          <Route path="/resources/mortgage-calculator" element={<MortgageCalculatorPage />} />
          <Route path="/fundraise-campaigns/:id" element={<PublicCampaignDetailsPage />} />
          <Route path="/fundraise-campaigns" element={<PublicFundRaiseCampaignsPage />} />
          <Route path="/events" element={<PublicEventsPage />} />
          <Route path="/events/:id" element={<PublicEventDetailsPage />} />
          {/* <Route path="/public/funds-campaign" element={<PublicFundCampaignDetails />} /> */}


          {/* Footer routes */}
          <Route path="/tutorials/group-creation" element={<GroupCreationTutorial />} />
          <Route path="/tutorials/manage-members" element={<ManageMembersTutorial />} />
          <Route path="/tutorials/group-management" element={<GroupManagementTutorial />} />
          <Route path="/tutorials/event-creation" element={<EventCreationTutorial />} />
          <Route path="/legal/documents/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/legal/documents/disclosure" element={<DisclosurePolicyPage />} />
          <Route path="/legal/documents/terms-and-conditions" element={<TermsAndConditionsPage />} />
          <Route path="/legal/documents/aml-ctf" element={<AmlCtfPage />} />
          <Route path="/legal/documents/security" element={<SecurityPolicyPage />} />
          <Route path="/legal/documents/cookie" element={<CookiePolicyPage />} />
          <Route path="/legal/documents/acceptable-use-policy" element={<AcceptableUsePolicyPage />} />

          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/onboarding/login" element={<NewAdminLogin />} />
          <Route path="/admin/onboarding/registration" element={<GoogleAuthRegistration />} />


          {/* Authenticated Routes */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <DashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/service-plans"
            element={
              <PrivateRoute>
                <ServicePlansPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/groups/:groupId"
            element={
              <PrivateRoute>
                <GroupDetailsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/groups/join-group"
            element={
              <PrivateRoute>
                <JoinGroupPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/groups/create-group"
            element={
              <PrivateRoute>
                <CreateGroupPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/groups/enrolled-groups"
            element={
              <PrivateRoute>
                <EnrolledGroupsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/groups/submitted-requests"
            element={
              <PrivateRoute>
                <SubmittedMembershipRequests />
              </PrivateRoute>
            }
          />
          <Route
            path="/transactions"
            element={
              <PrivateRoute>
                <TransactionsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/disputes"
            element={
              <PrivateRoute>
                <MyDisputesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/disputes/:disputeId"
            element={
              <PrivateRoute>
                <DisputeDetailsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/account/setup"
            element={
              <PrivateRoute>
                <AccountProfileSetup />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/organizer/setup"
            element={
              <PrivateRoute>
                <OrganizerProfileSetup />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/organizer/profiles"
            element={
              <PrivateRoute>
                <OrganizerProfilesList />
              </PrivateRoute>
            }
          />
          <Route
            path="/groups/:groupId/fund-requests"
            element={
              <PrivateRoute>
                <FundRequestHistoryPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/groups/:groupId/requests/:requestId"
            element={
              <PrivateRoute>
                <FundRequestDetailsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/courses/:courseId/modules/:moduleId/lessons/:lessonId"
            element={
              <PrivateRoute>
                <LessonPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/notifications/"
            element={
              <PrivateRoute>
                <NotificationsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/notifications/:id"
            element={
              <PrivateRoute>
                <NotificationDetailPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/contact-us"
            element={
              <PrivateRoute>
                <ContactUsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/unio-meeting"
            element={
              <PrivateRoute>
                <WebConferencePage />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/fundraise-campaigns"
            element={
              <PrivateRoute>
                <FundRaiseCampaignsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/fundraise-campaigns/:id"
            element={
              <PrivateRoute>
                <CampaignDetailsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard/fundraise-campaigns/create"
            element={
              <PrivateRoute>
                <CreateFundraiserCampaignPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/events"
            element={
              <PrivateRoute>
                <EventsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/events/:id"
            element={
              <PrivateRoute>
                <EventDetailsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/events/create"
            element={
              <PrivateRoute>
                <EventCreationPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/events/my-events"
            element={
              <PrivateRoute>
                <UserEventsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/events/event/:id/manage"
            element={
              <PrivateRoute>
                <OrganiserEventManagementDashboard />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/events/registered-events"
            element={
              <PrivateRoute>
                <RegisteredEventsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/events/:eventId/retry-purchase/:purchaseId"
            element={
              <PrivateRoute>
                <RetryTicketPurchasePage />
              </PrivateRoute>
            }
          />

          <Route
            path="/dashboard/fundraise-campaigns/my-campaigns"
            element={
              <PrivateRoute>
                <MyCampaigns />
              </PrivateRoute>
            }
          />


          {/*  ********         Admin routes  ***********************  */}
          <Route
            path="/admin/dashboard"
            element={
              <PrivateRoute adminOnly>
                <AdminDashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <PrivateRoute adminOnly>
                <UserManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/employees"
            element={
              <PrivateRoute adminOnly>
                <EmployeeManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/groups"
            element={
              <PrivateRoute adminOnly>
                <AdminGroupManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/tickets"
            element={
              <PrivateRoute adminOnly>
                <TicketManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/accounting"
            element={
              <PrivateRoute adminOnly>
                <AccountDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/fundraisers"
            element={
              <PrivateRoute adminOnly>
                <AdminCampaignDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/events"
            element={
              <PrivateRoute adminOnly>
                <AdminEventManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/nexus"
            element={
              <PrivateRoute adminOnly>
                <NexusReportPage />
              </PrivateRoute>
            }
          />
          {/* Alerts and obligations views are embedded in /admin/nexus as tabs */}
          <Route
            path="/admin/disputes"
            element={
              <PrivateRoute adminOnly>
                <AdminDisputeDashboard />
              </PrivateRoute>
            }
          />


          <Route
            path="/nexus"
            element={
                <NexusReportPage />
            }
          />

          {/* Delete this */}
          {/* <Route
            path="/meeting"
            element={
              <PrivateRoute>
                <TestPage />
              </PrivateRoute>
            }
          /> */}
          <Route path="/disputing" element={<AdminDisputeDashboard />} />


          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default AppRoutes;